import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Mail, Phone, MapPin, LogOut, Wallet, Gift, HelpCircle, Bell, Bike, ChefHat, Shield } from "lucide-react";

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, phone, address });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const links = [
    { to: "/orders", label: "Order history", icon: Wallet },
    { to: "/favorites", label: "Favorites", icon: Gift },
  ];
  if (user?.role === "driver") links.push({ to: "/driver", label: "Driver Dashboard", icon: Bike });
  if (user?.role === "restaurant") links.push({ to: "/restaurant-admin", label: "Restaurant Dashboard", icon: ChefHat });
  if (user?.role === "admin") links.push({ to: "/admin", label: "Admin Panel", icon: Shield });

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-32">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-3xl bg-eatup-charcoal text-white flex items-center justify-center font-display text-3xl font-bold overflow-hidden">
          {user?.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" /> : (user?.name || "U")[0]?.toUpperCase()}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-eatup-green font-bold">{user?.role}</div>
          <h1 className="font-display text-3xl font-bold mt-1">{user?.name}</h1>
          <div className="text-sm text-neutral-500">{user?.email}</div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl card-shadow p-6">
          <div className="text-xs uppercase tracking-widest font-bold text-eatup-green">Profile</div>
          <div className="mt-4 space-y-3">
            <Field icon={User} value={name} onChange={setName} placeholder="Name" testid="profile-name" />
            <Field icon={Mail} value={user?.email} disabled placeholder="Email" testid="profile-email" />
            <Field icon={Phone} value={phone} onChange={setPhone} placeholder="Phone" testid="profile-phone" />
            <Field icon={MapPin} value={address} onChange={setAddress} placeholder="Delivery address" testid="profile-address" />
          </div>
          <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="mt-5 btn-primary disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="bg-white rounded-3xl card-shadow p-6 flex flex-col">
          <div className="text-xs uppercase tracking-widest font-bold text-eatup-green">Shortcuts</div>
          <div className="mt-4 space-y-2 flex-1">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <button
                  key={l.to}
                  data-testid={`profile-link-${l.label.toLowerCase().replace(/\s/g,'-')}`}
                  onClick={() => navigate(l.to)}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-eatup-gray transition text-left"
                >
                  <div className="w-9 h-9 rounded-xl bg-eatup-green/10 text-eatup-green flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="font-semibold">{l.label}</span>
                </button>
              );
            })}
            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-eatup-gray transition text-left" data-testid="profile-notif">
              <div className="w-9 h-9 rounded-xl bg-eatup-green/10 text-eatup-green flex items-center justify-center"><Bell className="w-4 h-4" /></div>
              <span className="font-semibold">Notifications</span>
            </button>
            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-eatup-gray transition text-left" data-testid="profile-support">
              <div className="w-9 h-9 rounded-xl bg-eatup-green/10 text-eatup-green flex items-center justify-center"><HelpCircle className="w-4 h-4" /></div>
              <span className="font-semibold">Help & Support</span>
            </button>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-4 flex items-center gap-2 text-red-500 hover:text-red-600 font-semibold text-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, value, onChange, placeholder, disabled, testid }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none disabled:opacity-70"
      />
    </div>
  );
}
